import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking, paid, failed
  const [itinId, setItinId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("failed"); return; }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setItinId(data.itinerary_id);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (attempts.current >= 8) { setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#E4E7EC] bg-white p-10">
        {state === "checking" && (<>
          <Loader2 className="animate-spin mx-auto text-[#0D5C75]" size={44} />
          <h1 className="font-heading text-xl font-bold mt-4 text-[#101828]">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-1 text-sm">Funds are being isolated into escrow.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="mx-auto text-[#12B76A]" size={56} data-testid="payment-success-icon" />
          <h1 className="font-heading text-2xl font-bold mt-4 text-[#101828]">Payment secured in escrow</h1>
          <p className="text-muted-foreground mt-2 text-sm">Your bundle is confirmed. Providers are paid only as clinical milestones are met.</p>
          <div className="mt-6 flex flex-col gap-2">
            <Button className="rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white"
              onClick={() => navigate(itinId ? `/journey/${itinId}` : "/dashboard")} data-testid="view-journey-btn">View my journey</Button>
            <Button variant="ghost" onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
          </div>
        </>)}
        {state === "failed" && (<>
          <XCircle className="mx-auto text-destructive" size={56} />
          <h1 className="font-heading text-xl font-bold mt-4 text-[#101828]">We couldn't confirm payment</h1>
          <p className="text-muted-foreground mt-2 text-sm">If you were charged, check your dashboard shortly.</p>
          <Button className="mt-6 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white" onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
        </>)}
      </div>
    </div>
  );
}
